import React, {
  useEffect,
  useReducer,
  useRef,
  useState,
  useMemo,
  useCallback,
  useContext,
} from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
  Box,
  Flex,
  Text,
  useDisclosure,
  useToast,
  useColorModeValue,
  useBreakpointValue,
  HStack,
  VStack,
  Icon,
  Badge,
  Avatar,
  Divider,
  Container,
  Button,
  ButtonGroup,
  Skeleton,
  SkeletonText,
  SkeletonCircle,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Link as ChakraLink,
} from "@chakra-ui/react";
import TicketDetailMobile from "./TicketDetailMobile";
import { AddIcon } from "@chakra-ui/icons";
import { BsArrowUp } from "react-icons/bs";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Right,
} from "../../../../components/Card";
import { TicketState } from "../../../../components/States";
import {
  TwoColumns,
  LeftPanel,
  RightPanel,
} from "../../../../components/layouts/TwoColumns";
import {
  useLoaderData,
  useNavigate,
  useRevalidator,
  useSearchParams,
} from "react-router-dom";
import { translateKind } from "../../../../shared/libs/translate";
import moment from "moment";

import TicketActions from "../../../../components/TicketActions";
import InfoBox from "../../../../components/InfoBox";
import Tasks from "./Tasks";
import Customer from "./Customer";
import { MapContainer, TileLayer, Marker } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { ExternalLinkIcon } from "@chakra-ui/icons";
import AssignTicketModal from "./modals/AssignTicketModal";
import AssignUnitModal from "./modals/AssignUnitModal";
import InvoiceModal from "../../../../components/modals/InvoiceModal";
import CreateTaskModal from "./modals/CreateTaskModal";
import CreateBulkTaskModal from "./modals/CreateBulkTaskModal";
import Notes from "../../../../components/notes/Notes";
import Logs from "../../../../components/logs/Logs";
import InfoGrid from "../../../../components/InfoGrid";
import TicketTimeline from "./components/TicketTimeline";
import TicketAttachments from "./components/TicketAttachments";
import TicketHeader from "./components/TicketHeader";
import TicketDescription from "./components/TicketDescription";
import TasksSection from "./components/TasksSection";
import ActivityTabs from "./components/ActivityTabs";
import CustomerCard from "./components/CustomerCard";
import TicketPrintModal from "./modals/TicketPrintModal";
import client from "../../../../helpers/Client";
import urlToId from "../../../../helpers/UrlToId";
import { generateTicketPDFFromTemplate } from "../../../../utils/pdfTemplateGenerator";
import AuthContext from "../../../../contexts/Auth";
import {
  FaTicketAlt,
  FaUser,
  FaEnvelope,
  FaMapMarkerAlt,
  FaBuilding,
  FaCog,
  FaCalendarAlt,
  FaFire,
  FaClock,
  FaExclamationTriangle,
  FaCheckCircle,
  FaTimesCircle,
  FaPlayCircle,
  FaPauseCircle,
  FaComments,
  FaTasks,
  FaHistory,
  FaPhone,
  FaIdCard,
  FaPriority,
  FaTag,
  FaArrowUp,
  FaArrowDown,
  FaMinus,
  FaUpload,
  FaPlay,
} from "react-icons/fa";
// Leaflet marker icon fix for bundlers
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

export async function ticketLoader({ params }) {
  const ticketID = params.ticketID;
  const ticket = await client.get(`/tickets/${ticketID}/`);
  return {
    ticket: ticket.data,
    ticketID,
  };
}

function renderCountReducer(state) {
  return state + 1;
}

export default function Ticket() {
  const { t } = useTranslation();
  const { ticket, ticketID } = useLoaderData();
  const auth = useContext(AuthContext);
  const isSuperUser = auth?.data?.user?.is_superuser;
  const navigate = useNavigate();
  const revalidator = useRevalidator();
  const toast = useToast();
  const [searchParams] = useSearchParams();
  // Extract reseller ID from URL or assigned_to URL
  const getResellerID = () => {
    // First try URL parameter
    const urlResellerID = searchParams.get("bayi");
    if (urlResellerID) return urlResellerID;

    // Then try to extract from assigned_to URL
    if (ticket?.assigned_to?.url) {
      const match = ticket.assigned_to.url.match(/\/resellers\/(\d+)\//);
      if (match) return match[1];
    }

    return null;
  };

  const resellerID = getResellerID();
  const tasksRef = useRef();
  const ticketRef = useRef();
  const [ticketRendered, forceTicketRender] = useReducer(renderCountReducer, 0);
  const assignTicketModalState = useDisclosure();
  const assignUnitModalState = useDisclosure();
  const invoiceModalState = useDisclosure();
  const createTaskModalState = useDisclosure();
  const createBulkTaskModalState = useDisclosure();
  const serviceFormModalState = useDisclosure();
  const liveSupportModalState = useDisclosure();
  const autoOpenServiceForm = !!searchParams.get("servis_formu");

  // All hooks must be called at the top level
  const isMobile = useBreakpointValue({ base: true, md: false });
  const containerPadding = useBreakpointValue({ base: 4, md: 6, lg: 8 });
  const headerPadding = useBreakpointValue({ base: 4, md: 6, lg: 8 });
  const cardSpacing = useBreakpointValue({ base: 4, md: 6, lg: 8 });
  const titleSize = useBreakpointValue({ base: "xl", md: "2xl", lg: "3xl" });
  const subtitleSize = useBreakpointValue({ base: "md", md: "lg", lg: "xl" });
  const iconSize = useBreakpointValue({ base: 6, md: 7, lg: 8 });
  const cardPadding = useBreakpointValue({ base: 4, md: 6, lg: 8 });

  // Loading states
  const [isLoading, setIsLoading] = useState(false);
  const [componentsLoading, setComponentsLoading] = useState({
    tasks: false,
    notes: false,
    timeline: false,
    attachments: false,
    statistics: false,
    relatedTickets: false,
    logs: false,
  });
  const [error, setError] = useState(null);

  // Tab state for activity sections
  const [activeTab, setActiveTab] = useState(0);

  // Color mode values - must be called at component level
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const textColor = useColorModeValue("gray.800", "white");
  const mutedTextColor = useColorModeValue("gray.600", "gray.400");
  const quickInfoBg = useColorModeValue("gray.50", "gray.700");
  const quickInfoBorderColor = useColorModeValue("gray.200", "gray.600");
  const descriptionBg = useColorModeValue("gray.50", "gray.700");
  const descriptionBorderColor = useColorModeValue("gray.200", "gray.600");

  // Color scheme - memoized to prevent unnecessary recalculations
  const colorScheme = useMemo(
    () => ({
      headerBg: "linear(to-r, red.700, red.900)",
      cardBg,
      borderColor,
      textColor,
      mutedTextColor,
      quickInfoBg,
      quickInfoBorderColor,
      descriptionBg,
      descriptionBorderColor,
    }),
    [
      cardBg,
      borderColor,
      textColor,
      mutedTextColor,
      quickInfoBg,
      quickInfoBorderColor,
      descriptionBg,
      descriptionBorderColor,
    ]
  );

  // All callback functions - must be at component level
  const handleScrollToTasks = useCallback(() => {
    if (tasksRef.current) {
      tasksRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, []);

  const handleTabChange = useCallback((index) => {
    setActiveTab(index);
  }, []);

  const setComponentLoading = useCallback((component, loading) => {
    setComponentsLoading((prev) => ({
      ...prev,
      [component]: loading,
    }));
  }, []);

  const handlePrint = useCallback(async () => {
    try {
      // Kurumsal PDF template'ini kullanarak ticket bilgilerini ekle
      await generateTicketPDFFromTemplate(ticket, getStatusText, translateKind);
    } catch (error) {
      console.error("PDF oluşturma hatası:", error);
      // Fallback olarak basit PDF oluştur
      generateTicketPDF(ticket);
    }
  }, [ticket]);

  const handleCreateInvoice = useCallback(() => {
    invoiceModalState.onOpen();
  }, [invoiceModalState]);

  const buildDealerMessage = useCallback(() => {
    const id = ticket?.id || urlToId(ticket?.url);
    const workOrderURL = `https://destek-admin.inohom.cloud/is-emri/${urlToId(
      ticket?.url
    )}`;
    const dealerName = ticket?.assigned_to?.name || "-";
    const customerName = ticket?.full_name || "-";
    const customerPhone = ticket?.phone_number || "-";
    return `Sayın Bayimiz,
 
Helpdesk uygulamamızda tarafınıza atanmış ve hâlihazırda bekleyen iş emirleriniz bulunmaktadır. İşlerin güncel durumunu ilerletmeniz gerekmektedir.
 
Ticket ID: ${id}
 
İş Emri URL: ${workOrderURL}
 
Bayi Adı: ${dealerName}
 
Müşteri Adı: ${customerName}
 
Müşteri Telefonu: ${customerPhone}
 
Bilgilerinize sunar, gerekli aksiyonların alınmasını rica ederiz.
 
Saygılarımızla,
inohom Destek Ekibi`;
  }, [ticket]);

  const handleCopyDealerMessage = useCallback(async () => {
    const msg = buildDealerMessage();
    try {
      // Modern Clipboard API (güvenli bağlamlarda çalışır)
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(msg);
      } else {
        // Fallback: geçici textarea ile kopyalama
        const textarea = document.createElement("textarea");
        textarea.value = msg;
        textarea.style.position = "fixed";
        textarea.style.top = "-1000px";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(textarea);
        if (!ok) throw new Error("execCommand copy failed");
      }
      toast({
        title: t("messages.copied"),
        description: t("messages.message_template_copied"),
        status: "success",
        duration: 2500,
        isClosable: true,
      });
    } catch (e) {
      console.error("Clipboard copy error:", e);
      toast({
        title: t("messages.error"),
        description: t("messages.clipboard_copy_failed"),
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  }, [buildDealerMessage, toast]);

  const handleAction = useCallback(
    async (action, successMessage, callback) => {
      try {
        setIsLoading(true);
        setError(null);
        console.log("Sending action:", action, "to ticketID:", ticketID);
        console.log("Request URL:", `/tickets/${ticketID}/${action}/`);
        console.log("Request payload:", { confirm: true });

        const response = await client.post(`/tickets/${ticketID}/${action}/`, {
          confirm: true,
        });
        console.log("Response:", response);

        toast({
          title: t("messages.success"),
          description: successMessage,
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        revalidator.revalidate();
        forceTicketRender();
        if (callback) callback();
      } catch (error) {
        console.error("Action error:", error);
        console.error("Error response:", error.response);
        const errorMessage =
          error.response?.data?.detail ||
          error.response?.data?.non_field_errors?.[0] ||
          error.message ||
          t("messages.operation_failed");
        setError(errorMessage);
        toast({
          title: t("messages.error"),
          description: errorMessage,
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setIsLoading(false);
      }
    },
    [ticketID, toast, revalidator, forceTicketRender]
  );

  const handleScrollToTicket = useCallback(() => {
    if (ticketRef.current) {
      ticketRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, []);

  const handleDelete = useCallback(() => {
    handleAction("delete", t("messages.ticket_deleted"), () =>
      navigate("/is-emri/")
    );
  }, [handleAction, navigate, t]);

  const handleStart = useCallback(
    () => handleAction("start", t("messages.ticket_started")),
    [handleAction, t]
  );
  const handleComplete = useCallback(
    () => handleAction("complete", t("messages.ticket_completed")),
    [handleAction, t]
  );
  const handleCancel = useCallback(
    () => handleAction("cancel", t("messages.ticket_cancelled")),
    [handleAction, t]
  );

  const handleConfirmService = useCallback(
    async () => {
      revalidator.revalidate();
    },
    [revalidator]
  );

  // Priority mapping
  const priorityConfig = useMemo(() => {
    const getPriorityConfig = (priority) => {
      if (priority >= 4)
        return {
          icon: FaArrowUp,
          color: "red.500",
          label: t("priority.high"),
          bg: "red.50",
        };
      if (priority === 3)
        return {
          icon: FaMinus,
          color: "orange.500",
          label: t("priority.medium"),
          bg: "orange.50",
        };
      return {
        icon: FaArrowDown,
        color: "green.500",
        label: t("priority.low"),
        bg: "green.50",
      };
    };
    return getPriorityConfig(ticket.priority);
  }, [ticket.priority, t]);

  // Status mapping
  const statusConfig = useMemo(() => {
    const getStatusConfig = (state) => {
      switch (state) {
        case "new":
          return {
            icon: FaExclamationTriangle,
            color: "blue.500",
            label: t("status.new"),
          };
        case "assigned":
          return {
            icon: FaUser,
            color: "purple.500",
            label: t("status.assigned"),
          };
        case "in_progress":
          return {
            icon: FaPlayCircle,
            color: "orange.500",
            label: t("status.in_progress"),
          };
        case "completed":
          return {
            icon: FaCheckCircle,
            color: "green.500",
            label: t("status.completed"),
          };
        case "cancelled":
          return {
            icon: FaTimesCircle,
            color: "red.500",
            label: t("status.cancelled"),
          };
        default:
          return {
            icon: FaClock,
            color: "gray.500",
            label: t("status.unknown"),
          };
      }
    };
    return getStatusConfig(ticket.state);
  }, [ticket.state, t]);

  // Motion components - memoized to prevent recreation
  const MotionContainer = useMemo(() => motion(Container), []);
  const MotionBox = useMemo(() => motion(Box), []);
  const MotionFlex = useMemo(() => motion(Flex), []);
  const [formAttachments, setFormAttachments] = useState([]);
  const [formsLoading, setFormsLoading] = useState(false);

  const [meetingStartDate, setMeetingStartDate] = useState("");
  const [meetingStartTime, setMeetingStartTime] = useState("");
  const [creatingMeeting, setCreatingMeeting] = useState(false);
  const [createdMeeting, setCreatedMeeting] = useState(null);

  useEffect(() => {
    if (!liveSupportModalState.isOpen) return;
    const m = moment().add(30, "minutes").startOf("minute");
    setMeetingStartDate(m.format("YYYY-MM-DD"));
    setMeetingStartTime(m.format("HH:mm"));
    setCreatedMeeting(null);
  }, [liveSupportModalState.isOpen]);

  const suggestedMeetingSlots = useMemo(() => {
    const base = moment().add(30, "minutes");
    const rounded = base.minute(base.minute() < 30 ? 30 : 0).second(0).millisecond(0);
    if (base.minute() >= 30) rounded.add(1, "hour");
    return Array.from({ length: 4 }).map((_, i) => rounded.clone().add(i * 30, "minutes"));
  }, [liveSupportModalState.isOpen]);

  const handleCreateLiveSupportMeeting = async () => {
    if (!ticketID) return;
    const dt = moment(`${meetingStartDate} ${meetingStartTime}`, "YYYY-MM-DD HH:mm", true);
    if (!dt.isValid()) {
      toast({ title: "Tarih/saat geçersiz", status: "warning", duration: 3000, isClosable: true });
      return;
    }
    setCreatingMeeting(true);
    try {
      const resp = await client.post(`/tickets/${ticketID}/create_video_meeting/`, {
        start_time: dt.toISOString(),
      });
      setCreatedMeeting(resp.data || null);
      toast({ title: "Canlı destek randevusu oluşturuldu", status: "success", duration: 3000, isClosable: true });
      revalidator.revalidate();
    } catch (e) {
      toast({
        title: "Canlı destek oluşturulamadı",
        description: e?.response?.data?.error || e?.response?.data?.detail || e?.message || "İstek başarısız",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setCreatingMeeting(false);
    }
  };

  useEffect(() => {
    const loadForms = async () => {
      const tId = urlToId(ticket?.url) || ticket?.id;
      if (!tId) return;
      setFormsLoading(true);
      try {
        const resp = await client.get("/attachments/", {
          params: {
            object_model: "ticket.ticket",
            object_id: tId,
            page_size: 50,
          },
          disableCache: true,
        });
        const list = Array.isArray(resp?.data?.results)
          ? resp.data.results
          : Array.isArray(resp?.data)
            ? resp.data
            : [];
        setFormAttachments(list);
      } catch (_) {
        setFormAttachments([]);
      } finally {
        setFormsLoading(false);
      }
    };
    loadForms();
  }, [ticket]);

  useEffect(() => {
    if (searchParams.get("gorev")) {
      setTimeout(() => {
        handleScrollToTasks();
      }, 100);
    }
  }, [searchParams, handleScrollToTasks]);

  useEffect(() => {
    if (
      autoOpenServiceForm &&
      ticket &&
      ticket.number !== undefined &&
      ticket.number !== null
    ) {
      serviceFormModalState.onOpen();
    }
  }, [autoOpenServiceForm, ticket, serviceFormModalState]);

  // Use mobile component for small screens
  if (isMobile) {
    return (
      <TicketDetailMobile
        ticket={ticket}
        ticketID={ticketID}
        autoOpenServiceForm={autoOpenServiceForm}
        onAction={(action) => {
          // Handle action and revalidate
          revalidator.revalidate();
        }}
      />
    );
  }

  const generateTicketPDF = (ticketData) => {
    // PDF içeriği oluştur

    // Yeni pencere aç ve yazdır
    const printWindow = window.open("", "_blank");
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  const getStatusText = (state) => {
    const statusMap = {
      new: "Yeni",
      assigned: "Atanmış",
      started: "Başlatılmış",
      completed: "Tamamlanmış",
      cancelled: "İptal Edilmiş",
    };
    return statusMap[state] || state;
  };

  const getPriorityText = (priority) => {
    const priorities = {
      low: "Düşük",
      medium: "Orta",
      high: "Yüksek",
      urgent: "Acil",
    };
    return priorities[priority] || priority;
  };

  const translateKind = (kind) => {
    const kinds = {
      education: "Eğitim",

      malfunction: "Arıza",
      replacement: "Değişim",
      assembly: "Montaj",
      initialization: "Başlatım",
      maintenance: "Bakım",
      construction_site_control: "İnşaat Alanı Kontrol",
      infrastructure_control: "Altyapı Kontrol",
      other: "Diğer",
    };
    return kinds[kind] || kind;
  };

  return (
    <MotionContainer
      maxW="7xl"
      py={containerPadding}
      px={containerPadding}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
    >
      {/* Error Alert */}
      {error && (
        <Alert status="error" mb={6} borderRadius="lg">
          <AlertIcon />
          <Box>
            <AlertTitle>{t("messages.error")}!</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Box>
        </Alert>
      )}

      {/* Global Loading Overlay */}
      {isLoading && (
        <Box
          position="fixed"
          top={0}
          left={0}
          right={0}
          bottom={0}
          bg="blackAlpha.300"
          zIndex={9999}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Box
            bg={colorScheme.cardBg}
            p={6}
            borderRadius="xl"
            shadow="xl"
            display="flex"
            alignItems="center"
            gap={4}
          >
            <Spinner size="lg" color="blue.500" />
            <Text fontSize="lg" fontWeight="semibold">
              {t("messages.processing_operation")}
            </Text>
          </Box>
        </Box>
      )}

      {/* Enhanced Ticket Header */}
      <TicketHeader
        ticket={ticket}
        colorScheme={colorScheme}
        headerPadding={headerPadding}
        titleSize={titleSize}
        iconSize={iconSize}
        statusConfig={statusConfig}
        MotionBox={MotionBox}
        assignTicketModalState={assignTicketModalState}
        assignUnitModalState={assignUnitModalState}
        handleDelete={handleDelete}
        handleStart={handleStart}
        handleComplete={handleComplete}
        handleCancel={handleCancel}
        handlePrint={handlePrint}
        handleCreateInvoice={handleCreateInvoice}
        handleScrollToTasks={handleScrollToTasks}
        serviceFormModalState={serviceFormModalState}
        liveSupportModalState={liveSupportModalState}
      />

      {isSuperUser && (
        <Flex justify="flex-end" px={headerPadding} mt={2}>
          <Button
            size="sm"
            colorScheme="blue"
            onClick={handleCopyDealerMessage}
          >
            {t("buttons.send_message_copy_to_clipboard")}
          </Button>
        </Flex>
      )}

      {/* Main Content Area */}
      <MotionFlex
        as={TwoColumns}
        spacing={cardSpacing}
        direction={{ base: "column", lg: "row" }}
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2, ease: "easeOut" }}
      >
        <LeftPanel
          flex={{ base: "1", lg: "2" }}
          minW={{ base: "100%", lg: "0" }}
        >
          <VStack spacing={cardSpacing} align="stretch">
            {/* Enhanced Ticket Description */}
            <TicketDescription
              ticket={ticket}
              colorScheme={colorScheme}
              cardPadding={cardPadding}
              MotionBox={MotionBox}
            />

            <TasksSection
              ticket={ticket}
              resellerID={resellerID}
              MotionBox={MotionBox}
              colorScheme={colorScheme}
              createBulkTaskModalState={createBulkTaskModalState}
              createTaskModalState={createTaskModalState}
              handleScrollToTicket={handleScrollToTicket}
              componentsLoading={componentsLoading}
              setComponentLoading={setComponentLoading}
              tasksRef={tasksRef}
              ticketRendered={ticketRendered}
              revalidator={revalidator}
              ticketRef={ticketRef}
            />
            <ActivityTabs
              MotionBox={MotionBox}
              colorScheme={colorScheme}
              activeTab={activeTab}
              handleTabChange={handleTabChange}
              cardPadding={cardPadding}
              componentsLoading={componentsLoading}
              setComponentLoading={setComponentLoading}
              ticketID={ticketID}
              ticket={ticket}
              revalidator={revalidator}
            />
          </VStack>
        </LeftPanel>
        <RightPanel
          flex={{ base: "1", lg: "1" }}
          minW={{ base: "100%", lg: "0" }}
        >
          <VStack spacing={cardSpacing} align="stretch">
            {!!(ticket?.latitude && ticket?.longitude) && (
              <Box
                bg={cardBg}
                border="1px solid"
                borderColor={borderColor}
                borderRadius="2xl"
                overflow="hidden"
                shadow="xl"
              >
                <Box px={6} py={4} borderBottom="1px solid" borderColor={borderColor}>
                  <HStack justify="space-between" align="center">
                    <HStack spacing={3}>
                      <Icon as={FaMapMarkerAlt} color="red.500" />
                      <Text fontWeight="bold">Konum</Text>
                    </HStack>
                    <HStack spacing={2}>

                      <Button
                        size="sm"
                        rightIcon={<ExternalLinkIcon />}
                        onClick={() => {
                          const lat = Number(ticket.latitude);
                          const lng = Number(ticket.longitude);
                          const url = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
                          window.open(url, "_blank", "noopener,noreferrer");
                        }}
                      >
                        Yol Tarifi
                      </Button>
                    </HStack>
                  </HStack>
                </Box>
                <Box height="260px">
                  <MapContainer
                    center={[Number(ticket.latitude), Number(ticket.longitude)]}
                    zoom={15}
                    zoomAnimation={false}
                    style={{ height: "100%", width: "100%" }}
                  >
                    <TileLayer
                      url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                      attribution="Tiles © Esri"
                    />
                    <Marker position={[Number(ticket.latitude), Number(ticket.longitude)]} />
                  </MapContainer>
                </Box>
              </Box>
            )}
            <CustomerCard
              ticket={ticket}
              ticketID={ticketID}
              colorScheme={colorScheme}
              cardPadding={cardPadding}
            />
            <Card>
              <CardHeader>
                <CardTitle>Oluşturulmuş Formlar</CardTitle>
                <Right>{formsLoading ? <Spinner size="sm" /> : null}</Right>
              </CardHeader>
              <CardContent>
                {formAttachments.length === 0 ? (
                  <Text fontSize="sm" color="gray.500">
                    Bu iş emrine ait form bulunmuyor.
                  </Text>
                ) : (
                  <VStack align="stretch" spacing={3}>
                    {formAttachments.map((att) => {
                      const name = att?.file_name || "servis_formu.pdf";
                      let href = att?.file_url || att?.file || "";
                      const base = (
                        process.env.REACT_APP_API_BASE_URL || ""
                      ).replace(/\/$/, "");
                      if (href && !/^https?:\/\//.test(href)) {
                        href =
                          base + (href.startsWith("/") ? href : `/${href}`);
                      }
                      return (
                        <HStack key={att?.url || name} justify="space-between">
                          <VStack align="start" spacing={0}>
                            <Text fontSize="sm" fontWeight="500">
                              {att?.description || name}
                            </Text>
                            <Text fontSize="xs" color="gray.500">
                              {new Date(
                                att?.created_at || Date.now()
                              ).toLocaleString("tr-TR")}
                            </Text>
                          </VStack>
                          {href ? (
                            <Button
                              as="a"
                              href={href}
                              download
                              target="_blank"
                              size="sm"
                              variant="outline"
                            >
                              PDF İndir
                            </Button>
                          ) : (
                            <Text fontSize="xs" color="gray.400">
                              Bağlantı yok
                            </Text>
                          )}
                        </HStack>
                      );
                    })}
                  </VStack>
                )}
              </CardContent>
            </Card>
          </VStack>
        </RightPanel>
      </MotionFlex>
      <AssignTicketModal
        ticket={ticket}
        state={assignTicketModalState}
        revalidator={revalidator}
      />
      <AssignUnitModal
        ticket={ticket}
        state={assignUnitModalState}
        revalidator={revalidator}
      />
      <CreateTaskModal
        ticketURL={ticket.url}
        resellerID={resellerID}
        state={createTaskModalState}
        ticketRevalidator={revalidator}
      />
      <CreateBulkTaskModal
        ticketURL={ticket.url}
        resellerID={resellerID}
        state={createBulkTaskModalState}
        ticketRevalidator={revalidator}
      />
      <InvoiceModal
        isOpen={invoiceModalState.isOpen}
        onClose={invoiceModalState.onClose}
        ticket={ticket}
      />

      <Modal isOpen={liveSupportModalState.isOpen} onClose={liveSupportModalState.onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Canlı Destek</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={4}>
              <Text fontSize="sm" color="gray.600">
                Randevu seçince sistem görüşme linkini oluşturur ve müşteriye SMS ile gönderir.
              </Text>

              {ticket?.meeting_link ? (
                <Box border="1px" borderColor="gray.200" borderRadius="md" p={3}>
                  <Text fontWeight="600" mb={1}>
                    Mevcut Görüşme
                  </Text>
                  <ChakraLink href={ticket.meeting_link} isExternal color="blue.600">
                    Linki Aç <ExternalLinkIcon mx="2px" />
                  </ChakraLink>
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    {ticket.meeting_start_time ? new Date(ticket.meeting_start_time).toLocaleString("tr-TR") : ""}
                  </Text>
                </Box>
              ) : null}

              {createdMeeting?.meeting_link || createdMeeting?.support_link ? (
                <Box border="1px" borderColor="green.200" borderRadius="md" p={3} bg="green.50">
                  <Text fontWeight="700" mb={1}>
                    Oluşturulan Link
                  </Text>
                  <ChakraLink href={createdMeeting.support_link || createdMeeting.meeting_link} isExternal color="green.700">
                    Linki Aç <ExternalLinkIcon mx="2px" />
                  </ChakraLink>
                  {createdMeeting?.meeting_link && createdMeeting?.support_link ? (
                    <Box mt={3}>
                      <Text fontSize="sm" fontWeight="600" mb={1}>
                        Müşteri Linki (SMS ile giden)
                      </Text>
                      <ChakraLink href={createdMeeting.meeting_link} isExternal color="green.700">
                        Müşteri Linkini Aç <ExternalLinkIcon mx="2px" />
                      </ChakraLink>
                      <Text fontSize="sm" fontWeight="600" mt={3} mb={1}>
                        Destek Linki (Senin)
                      </Text>
                      <ChakraLink href={createdMeeting.support_link} isExternal color="green.700">
                        Destek Linkini Aç <ExternalLinkIcon mx="2px" />
                      </ChakraLink>
                    </Box>
                  ) : null}
                  <Text fontSize="xs" color="green.800" mt={1}>
                    SMS gönderildi.
                  </Text>
                </Box>
              ) : null}

              <Box>
                <Text fontSize="sm" fontWeight="600" mb={2}>
                  Önerilen Saatler
                </Text>
                <HStack wrap="wrap" spacing={2}>
                  {suggestedMeetingSlots.map((m) => (
                    <Button
                      key={m.toISOString()}
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setMeetingStartDate(m.format("YYYY-MM-DD"));
                        setMeetingStartTime(m.format("HH:mm"));
                      }}
                    >
                      {m.format("DD.MM")} {m.format("HH:mm")}
                    </Button>
                  ))}
                </HStack>
              </Box>

              <HStack spacing={3}>
                <FormControl>
                  <FormLabel>Tarih</FormLabel>
                  <Input type="date" value={meetingStartDate} onChange={(e) => setMeetingStartDate(e.target.value)} />
                </FormControl>
                <FormControl>
                  <FormLabel>Saat</FormLabel>
                  <Input type="time" value={meetingStartTime} onChange={(e) => setMeetingStartTime(e.target.value)} />
                </FormControl>
              </HStack>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="outline" mr={3} onClick={liveSupportModalState.onClose}>
              Kapat
            </Button>
            <Button colorScheme="teal" onClick={handleCreateLiveSupportMeeting} isLoading={creatingMeeting}>
              Davet Linki Oluştur
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <TicketPrintModal
        isOpen={serviceFormModalState.isOpen}
        onClose={serviceFormModalState.onClose}
        ticket={ticket}
        onConfirmService={handleConfirmService}
      />
    </MotionContainer>
  );
}
